import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Check } from 'lucide-react'
import { getAccounts } from '../../api/master'
import { listTransactionTypes, recordTransactionType } from '../../api/accounting'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { BackLink, DocHeader } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { fmtDay, localToday } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num } from '../sales/salesShared'
import { isHeading } from '../master/masterShared'
import { split } from './transactionTypes'

const back = <BackLink to="/accounting/transaction-types" label="Transaction types" />

// Record one of your transaction types: pick it, enter the amount, date and
// a note, and (where the type allows) choose other accounts. The entry is
// shown before it's posted.
export default function RecordTransaction() {
  const [params, setParams] = useSearchParams()
  const nav = useNavigate()
  const { notify } = useToast()
  const [types, setTypes] = useState(null)
  const [accounts, setAccounts] = useState([])
  const [date, setDate] = useState(localToday())
  const [amount, setAmount] = useState('')
  const [memo, setMemo] = useState('')
  const [picks, setPicks] = useState({})
  const [error, setError] = useState('')
  const [posting, setPosting] = useState(false)

  useEffect(() => {
    listTransactionTypes(true).then(setTypes).catch((e) => { setTypes([]); setError(e.message || 'Couldn’t load the transaction types.') })
    getAccounts().then((a) => setAccounts(Array.isArray(a) ? a : [])).catch(() => setAccounts([]))
  }, [])

  const typeId = params.get('type') || ''
  const type = (types || []).find((t) => String(t.type_id) === typeId)
  const byCode = useMemo(() => Object.fromEntries(accounts.map((a) => [a.account_code, a])), [accounts])
  const postable = useMemo(() => accounts.filter((a) => !isHeading(a, accounts) && !/^\d{2}00$/.test(String(a.account_code))), [accounts])
  const changeable = type ? type.lines.filter((l) => l.can_change) : []

  const preview = useMemo(() => {
    if (!type || !num(amount)) return null
    try { return { lines: split(type.lines, num(amount), picks) } } catch (e) { return { note: e.message } }
  }, [type, amount, picks])

  const pickType = (id) => {
    setPicks({})
    setError('')
    setParams(id ? { type: id } : {}, { replace: true })
  }

  const post = async () => {
    setPosting(true)
    setError('')
    try {
      const res = await recordTransactionType(type.type_id, {
        entry_date: date, amount: num(amount), memo: memo.trim(),
        accounts: Object.fromEntries(Object.entries(picks).filter(([, c]) => c)),
      })
      notify(`Posted ${docNo('je', res.je_no)}`)
      nav(`/accounting/journal/${res.je_id}`)
    } catch (e) {
      setError(e.message || 'Couldn’t record it.')
    } finally {
      setPosting(false)
    }
  }

  if (!types) return <div className="ci-form-page"><DocHeader back={back} title="Record a transaction" /><Spinner label="Loading…" /></div>
  if (!types.length) {
    return (
      <div className="ci-form-page">
        <DocHeader back={back} title="Record a transaction" />
        {error ? <ErrorBanner message={error} /> : <p className="mf-notice">No transaction types yet. <Link to="/accounting/transaction-types/new">Set one up</Link> first.</p>}
      </div>
    )
  }

  const totalDr = (preview?.lines || []).reduce((s, l) => s + l.dr, 0)
  const totalCr = (preview?.lines || []).reduce((s, l) => s + l.cr, 0)
  return (
    <div className="ci-form-page">
      <DocHeader back={back} title="Record a transaction" meta={type?.description || 'Choose the kind of transaction, then the amount.'} />
      {error && <ErrorBanner message={error} />}

      <div className="ci-form-meta-card">
        <div className="ci-form-meta-top two">
          <label className="ci-form-field">
            <span>Transaction type</span>
            <select value={typeId} onChange={(e) => pickType(e.target.value)}>
              <option value="">Choose one…</option>
              {types.map((t) => <option key={t.type_id} value={t.type_id}>{t.name}</option>)}
            </select>
          </label>
          <label className="ci-form-field">
            <span>Date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
        </div>
        <div className="ci-form-meta-top two">
          <label className="ci-form-field">
            <span>Amount</span>
            <input value={amount} inputMode="decimal" placeholder="0.00" onChange={(e) => setAmount(e.target.value)} />
          </label>
          <label className="ci-form-field">
            <span>Note (optional)</span>
            <input value={memo} maxLength={200} onChange={(e) => setMemo(e.target.value)} placeholder="e.g. Gift card 0042 for Ada" />
          </label>
        </div>
        {changeable.length > 0 && (
          <div className="ci-form-meta-top two">
            {changeable.map((l) => (
              <label key={l.line_id} className="ci-form-field">
                <span>{l.label || `${l.side === 'debit' ? 'Debit' : 'Credit'} account`}</span>
                <select value={picks[l.line_id] || ''} onChange={(e) => setPicks((p) => ({ ...p, [l.line_id]: e.target.value }))}>
                  <option value="">{l.account_code} · {l.account_name}</option>
                  {postable.filter((a) => a.type === l.account_type && a.account_code !== l.account_code)
                    .map((a) => <option key={a.account_code} value={a.account_code}>{a.account_code} · {a.account_name}</option>)}
                </select>
              </label>
            ))}
          </div>
        )}
      </div>

      {type && (
        <section className="doc-card">
          <div className="doc-card-head"><h2>This posts</h2><span>A journal entry dated {date ? fmtDay(date) : 'today'} <HelpTip text="It goes straight into the books. If it’s wrong, void it from Journal entries and record it again." /></span></div>
          {!preview ? <p className="doc-empty">Enter the amount to see the entry.</p> : preview.note ? <p className="doc-empty">{preview.note}</p> : (
            <div className="doc-table-scroll">
              <table className="ci-line-table tt-entry-table">
                <thead><tr><th>Account</th><th className="num">Debit</th><th className="num">Credit</th></tr></thead>
                <tbody>
                  {preview.lines.map((l) => (
                    <tr key={l.key}>
                      <td>{l.account_code} · {byCode[l.account_code]?.account_name || ''}{l.label && <span className="cell-sub">{l.label}</span>}</td>
                      <td className="num">{l.dr ? money(l.dr) : ''}</td>
                      <td className="num">{l.cr ? money(l.cr) : ''}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="acc-je-totals"><td>Totals</td><td className="num">{money(totalDr)}</td><td className="num">{money(totalCr)}</td></tr>
                </tfoot>
              </table>
            </div>
          )}
        </section>
      )}

      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button data-write type="button" className="ci-btn ci-btn-primary" disabled={posting || !preview?.lines || !date} onClick={post}><Check size={14} />{posting ? 'Posting…' : 'Post'}</button>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav('/accounting/transaction-types')}>Cancel</button>
        </div>
      </div>
    </div>
  )
}
